import { useState } from "react";
import { Edit2, Trash2 } from "lucide-react";
import {
  PARTNER_TIER_LABEL,
  PARTNER_TYPE_LABEL,
  type PartnerTier,
  type PartnerType,
  type PortalContentKey,
  type PortalContentRow,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useDeletePortalContent } from "./use-delete-portal-content";
import { PortalContentDialog } from "./portal-content-dialog";

export function PortalContentRows({
  rows,
  contentKey,
}: {
  rows: PortalContentRow[];
  contentKey: PortalContentKey;
}) {
  if (rows.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        No rows for this key yet. Add one above.
      </div>
    );
  }

  return (
    <ul className="divide-y">
      {rows.map((row) => (
        <PortalContentRowItem key={row.id} row={row} contentKey={contentKey} />
      ))}
    </ul>
  );
}

function PortalContentRowItem({
  row,
  contentKey,
}: {
  row: PortalContentRow;
  contentKey: PortalContentKey;
}) {
  const [editOpen, setEditOpen] = useState(false);
  const mutation = useDeletePortalContent();

  const preview = buildPreview(row, contentKey);

  return (
    <li className="flex items-start justify-between gap-4 px-5 py-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {scopeBadge(row)}
          <Badge variant="secondary" className="text-[10px]">
            position {row.position}
          </Badge>
          {row.active ? null : (
            <Badge variant="outline" className="text-[10px] text-muted-foreground">
              inactive
            </Badge>
          )}
        </div>
        {preview ? (
          <p className="mt-1.5 truncate text-sm font-medium text-foreground">
            {preview}
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditOpen(true)}
          className="h-8"
        >
          <Edit2 className="mr-1 h-3.5 w-3.5" />
          Edit
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" />
              Delete
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this content row?</AlertDialogTitle>
              <AlertDialogDescription>
                Partners scoped to this row will fall back to the next-most-
                specific match (or the default copy if none exists). This
                action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={mutation.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => mutation.mutate({ id: row.id })}
                disabled={mutation.isPending}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {editOpen ? (
        <PortalContentDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          mode={{ kind: "edit", row }}
        />
      ) : null}
    </li>
  );
}

function scopeBadge(row: PortalContentRow) {
  if (row.scope_kind === "all") {
    return (
      <Badge variant="outline" className="text-[10px]">
        All partners
      </Badge>
    );
  }
  if (row.scope_kind === "tier" && row.scope_value) {
    return (
      <Badge variant="outline" className="text-[10px]">
        Tier ·{" "}
        {PARTNER_TIER_LABEL[row.scope_value as PartnerTier] ?? row.scope_value}
      </Badge>
    );
  }
  if (row.scope_kind === "partner_type" && row.scope_value) {
    return (
      <Badge variant="outline" className="text-[10px]">
        Type ·{" "}
        {PARTNER_TYPE_LABEL[row.scope_value as PartnerType] ?? row.scope_value}
      </Badge>
    );
  }
  return null;
}

function buildPreview(row: PortalContentRow, key: PortalContentKey): string | null {
  if (key === "welcome") {
    const c = row.content as { title?: string };
    return c.title ?? null;
  }
  if (key === "level_up") {
    const c = row.content as { heading?: string };
    return c.heading ?? null;
  }
  if (key === "support_links") {
    const c = row.content as { heading?: string; links?: { label: string }[] };
    const count = c.links?.length ?? 0;
    return `${c.heading ?? "(no heading)"} — ${count} ${count === 1 ? "link" : "links"}`;
  }
  return null;
}
